import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    AppHeader,
    Button,
    ColumnDataType,
    DataTable,
    Modal,
    MultiSelect,
    Searchbox,
    Spinner,
} from "@tag/tag-components-react-v4";
import { DocList2Filled } from "@tag/tag-icons";
import UsageTelemetryService from "../services/UsageTelemetryService";
import { UsageTelemetryEnvironmentOption, UsageTelemetryStatus } from "../types/UsageTelemetry";
import { ProjectAccents } from "../../General/config/projectAccents";
import styles from "./UsageTelemetry.module.scss";

const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatShortDate(value: unknown): string {
    if (typeof value !== 'string' || value.length === 0) return '';
    const d = new Date(value.replace(' ', 'T'));
    if (isNaN(d.getTime())) return value;
    const day = d.getDate();
    const month = MONTH_NAMES[d.getMonth()];
    const year = String(d.getFullYear()).slice(2);
    return `${day} ${month} ${year}`;
}

function renderDateCell({ getValue }: any) {
    return <>{formatShortDate(getValue())}</>;
}

const COLUMN_CACHE_KEY = 'acpp-usage-telemetry-columns';

function UsageTelemetry() {
    const [generating, setGenerating] = useState<boolean>(false);
    const [progressMessage, setProgressMessage] = useState<string | undefined>();
    const [progressPercent, setProgressPercent] = useState<number | undefined>();
    const [taskId, setTaskId] = useState<string | undefined>();
    const [rows, setRows] = useState<any[] | undefined>();
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [alert, setAlert] = useState<{ type: any; message: string } | undefined>();

    const [environments, setEnvironments] = useState<UsageTelemetryEnvironmentOption[]>([]);
    const [selectedEnvironments, setSelectedEnvironments] = useState<string[]>([]);

    useEffect(() => {
        let cancelled = false;
        UsageTelemetryService.getEnvironments()
            .then(envs => {
                if (cancelled) return;
                setEnvironments(envs);
                setSelectedEnvironments(envs.filter(e => e.isDefault).map(e => e.name));
            })
            .catch(() => {
                if (cancelled) return;
                setEnvironments([]);
                setSelectedEnvironments([]);
            });
        return () => { cancelled = true; };
    }, []);

    const filteredRows = useMemo(() => {
        if (!rows) return [];
        const needle = searchTerm.trim().toLowerCase();
        if (!needle) return rows;
        return rows.filter(r => (r.workspaceOrganisationName ?? '').toLowerCase().includes(needle));
    }, [rows, searchTerm]);

    const columns = React.useMemo(
        () => [
            { accessorKey: 'environmentName', header: 'Environment' },
            { accessorKey: 'workspaceOrgId', header: 'Workspace Org Id' },
            { accessorKey: 'workspaceOrganisationName', header: 'Organisation Name' },
            { accessorKey: 'eventCount', header: 'Event Count', meta: { dataType: 'number' as ColumnDataType } },
            { accessorKey: 'lastEventTime', header: 'Last Event Time', cell: renderDateCell },
            { accessorKey: 'lastPublishedDate', header: 'Last Published Date', cell: renderDateCell },
            { accessorKey: 'lastAcceptanceDate', header: 'Last Acceptance Date', cell: renderDateCell },
            { accessorKey: 'activeUserCount', header: 'Active Users', meta: { dataType: 'number' as ColumnDataType } },
        ],
        []
    );

    const canDownload = !!taskId && rows !== undefined && rows.length > 0;

    const handleGenerate = async () => {
        setAlert(undefined);
        setRows(undefined);
        setTaskId(undefined);
        setGenerating(true);
        setProgressMessage('Starting…');
        setProgressPercent(undefined);

        try {
            const { taskId: newTaskId } = await UsageTelemetryService.generate(selectedEnvironments);

            let status: UsageTelemetryStatus;
            do {
                await delay(2000);
                status = await UsageTelemetryService.getStatus(newTaskId);
                setProgressPercent(status.progress ?? undefined);
                setProgressMessage(status.progressMessage ?? 'Working…');
            } while (!status.complete);

            if (status.errorMessage) {
                setAlert({ type: 'error', message: status.errorMessage });
            } else {
                setRows(status.rows ?? []);
                setTaskId(newTaskId);
                setAlert({
                    type: 'success',
                    message: `Report generated — ${status.orgsReturned} organisations, ${status.environmentsFailed} environment(s) failed.`
                });
            }
        } catch (e) {
            setAlert({
                type: 'error',
                message: 'Failed to generate the usage telemetry report. Please try again or contact HSC Care DevOps.'
            });
        } finally {
            setGenerating(false);
        }
    };

    const handleDownload = async () => {
        if (!taskId) return;
        try {
            const { blob, filename } = await UsageTelemetryService.downloadReport(taskId);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (e) {
            setAlert({
                type: 'error',
                message: 'Download failed. The report may have expired — regenerate and try again.'
            });
        }
    };

    return (
        <div className={styles.Page}>
            <div className={styles.Header}>
                <h1>Usage Telemetry</h1>
                <h4>Generate a usage telemetry report across every Policies &amp; Procedures organisation configured for this environment.</h4>
            </div>

            <AppHeader
                heading="Policy Telemetry Report"
                icon={<DocList2Filled />}
                accent={ProjectAccents.PoliciesAndProcedures}
            >
                <div className={styles.Controls}>
                    <Button
                        text="Generate usage telemetry report"
                        accent={ProjectAccents.PoliciesAndProcedures}
                        disabled={generating || (environments.length > 0 && selectedEnvironments.length === 0)}
                        onClick={handleGenerate}
                    />
                    {canDownload ? (
                        <Button
                            text="Download report"
                            accent={ProjectAccents.PoliciesAndProcedures}
                            onClick={handleDownload}
                        />
                    ) : null}
                </div>
            </AppHeader>

            {alert ? (
                <div className={styles.AlertBlock}>
                    <Alert type={alert.type} closeable onClosed={() => setAlert(undefined)}>
                        {alert.message}
                    </Alert>
                </div>
            ) : null}

            <div className={styles.TableBlock}>
                <div className={styles.TableToolbar}>
                    <Searchbox
                        value={searchTerm}
                        onSearch={(value) => setSearchTerm(value)}
                        clearButton
                        placeholder="Search by organisation name"
                        style={{ width: '320px' }}
                    />
                    {environments.length > 0 ? (
                        <MultiSelect
                            data={environments}
                            textField="name"
                            valueField="name"
                            value={selectedEnvironments}
                            onChange={(e: any) => setSelectedEnvironments((e?.value ?? []) as string[])}
                            placeholder="Environments"
                            selectionType="checkbox"
                            style={{ width: '320px' }}
                        />
                    ) : null}
                    {rows !== undefined ? (
                        <span className={styles.RowCount}>
                            {filteredRows.length} of {rows.length} organisations
                        </span>
                    ) : null}
                </div>
                <DataTable
                    isLoading={false}
                    columns={columns}
                    data={filteredRows}
                    columnPicker={{ cacheKey: COLUMN_CACHE_KEY }}
                    accent={ProjectAccents.PoliciesAndProcedures}
                    height="60vh"
                    noDataElement={
                        <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                            {rows === undefined ? (
                                <>Press <strong>Generate usage telemetry report</strong> to populate this table.</>
                            ) : searchTerm ? (
                                <>No organisations match &ldquo;{searchTerm}&rdquo;.</>
                            ) : (
                                <>No organisations to display.</>
                            )}
                        </div>
                    }
                />
            </div>

            <Modal
                visible={generating}
                accent={ProjectAccents.PoliciesAndProcedures}
                heading="Generating usage telemetry"
                suppressCloseButton
                primaryButtonProps={{ buttonStyle: { display: 'none' } }}
                cancelButtonProps={{ buttonStyle: { display: 'none' } }}
            >
                <div className={styles.ModalContent}>
                    <Spinner>{progressMessage ?? 'Working…'}</Spinner>
                    {typeof progressPercent === 'number' ? (
                        <span className={styles.ProgressText}>{progressPercent}%</span>
                    ) : null}
                </div>
            </Modal>
        </div>
    );
}

export default UsageTelemetry;
